/**
 * Uma pergunta ao assistente: `POST /mensagens` e o polling que o segue.
 *
 * 🔒 BR-25 — esta função só é chamada por ação explícita do usuário (enviar ou
 * clicar num chip). O polling é a CONTINUAÇÃO desse POST; nada aqui retoma um job
 * antigo, e nada é chamado de um efeito de montagem.
 *
 * Não é TanStack Query de propósito: mensagem de chat não é dado de servidor para
 * cachear, e uma chave de query não compraria nada.
 */
import { apiUser } from '@/api/clients/apiUser'
import { ErroDeApi } from '@/api/clients/criarCliente'
import {
  mensagemAceitaSchema,
  statusDaMensagemSchema,
  type CorpoDeMensagem,
} from '@/api/contracts/mensagens'

import { FalhaDoAssistente } from './falhas'
import { TEXTOS } from './textos'

export interface EntradaDeMensagem {
  idCliente: string
  /** `null` na primeira mensagem: o servidor cria a conversa. */
  idConversa: string | null
  mensagem: string
}

export interface RespostaDoAssistente {
  idConversa: string
  texto: string
  especialistasAcionados: string[]
}

interface Polling {
  /** Espera antes de cada consulta; a última se repete. */
  esperasMs: readonly number[]
  /**
   * Teto no CLIENTE. O backend já encerra sozinho: o worker desiste do agente aos
   * 55s, e um job ainda `processando` depois de 90s responde `falha` /
   * `EXECUCAO_EXPIRADA`. Isto é a rede para quando a CONSULTA é que não chega — rota
   * limitada (429) ou fora do ar por tempo demais.
   */
  tetoMs: number
  /** Falhas SEGUIDAS de rede ou 5xx toleradas numa consulta de status. */
  falhasToleradas: number
}

const POLLING_PADRAO: Polling = {
  esperasMs: [1000, 2000, 3000, 5000],
  tetoMs: 120_000,
  // Cinco, e não duas: o job está saudável e já foi cobrado, e uma rede que pisca
  // não pode jogar a resposta fora. Com as esperas padrão são ~16s de insistência.
  falhasToleradas: 5,
}

let polling: Polling = POLLING_PADRAO

/** Só para a suíte: zera as esperas. `null` devolve o padrão (`test/setup.ts`). */
export function definirPollingParaTeste(valor: Partial<Polling> | null): void {
  polling = valor ? { ...POLLING_PADRAO, ...valor } : POLLING_PADRAO
}

function dormir(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const abortar = () => {
      clearTimeout(temporizador)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    const temporizador = setTimeout(() => {
      signal.removeEventListener('abort', abortar)
      resolve()
    }, ms)
    signal.addEventListener('abort', abortar, { once: true })
  })
}

export async function enviarMensagem(
  entrada: EntradaDeMensagem,
  { signal }: { signal: AbortSignal },
): Promise<RespostaDoAssistente> {
  const corpo: CorpoDeMensagem = {
    idCliente: entrada.idCliente,
    mensagem: entrada.mensagem,
    ...(entrada.idConversa ? { idConversa: entrada.idConversa } : {}),
  }

  const aceita = mensagemAceitaSchema.safeParse(
    await apiUser.post<unknown>('/mensagens', corpo, {
      signal,
      // Uma chave por ENVIO. É o que deixa o backend reconhecer a MESMA requisição
      // repetida (um reenvio de rede); quem impede dois envios é o `Assistente`.
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),
  )
  if (!aceita.success) throw new Error(TEXTOS.semResposta)

  const { idConversa, idMensagem } = aceita.data
  const inicio = Date.now()
  let falhasSeguidas = 0

  for (let consulta = 0; ; consulta += 1) {
    const espera = polling.esperasMs[Math.min(consulta, polling.esperasMs.length - 1)] ?? 0
    if (Date.now() - inicio + espera >= polling.tetoMs) {
      throw new FalhaDoAssistente(TEXTOS.tempo, idConversa)
    }
    await dormir(espera, signal)

    let bruto: unknown
    try {
      bruto = await apiUser.get<unknown>(`/mensagens/${encodeURIComponent(idMensagem)}`, {
        params: { idConversa },
        signal,
      })
    } catch (erro) {
      // Quem abortou não quer saber do resultado. `signal.aborted`, e não o tipo do
      // erro: no vitest o `DOMException` do fetch vem de outro realm e o
      // `instanceof` de `criarCliente` não o reconhece.
      if (signal.aborted) throw erro

      const status = erro instanceof ErroDeApi ? erro.status : 0
      // 429 aqui é "consulte mais devagar": o gateway limita `/mensagens` a 5 req/s,
      // somando todos os usuários. O job segue saudável, então isto NÃO conta como
      // falha — volta a esperar, e quem encerra uma espera sem fim é o teto.
      if (status === 429) continue

      const transitoria = status === 0 || status >= 500
      falhasSeguidas += 1
      if (transitoria && falhasSeguidas <= polling.falhasToleradas) continue
      throw new FalhaDoAssistente(TEXTOS.semResposta, idConversa)
    }
    falhasSeguidas = 0

    const lido = statusDaMensagemSchema.safeParse(bruto)
    if (!lido.success) throw new FalhaDoAssistente(TEXTOS.semResposta, idConversa)

    const estado = lido.data
    if (estado.status === 'processando') continue
    if (estado.status === 'falha') {
      throw new FalhaDoAssistente(estado.erro.mensagem.trim() || TEXTOS.semResposta, idConversa)
    }

    const texto = estado.resposta?.trim()
    if (!texto) throw new FalhaDoAssistente(TEXTOS.semResposta, idConversa)
    return { idConversa, texto, especialistasAcionados: estado.especialistasAcionados }
  }
}
